import {computed, effect, EffectRef, EventEmitter, Injector, signal, untracked} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_TEXTURES} from "@common/services/logging"
import {Subject, takeUntil} from "rxjs"
import {AsyncReentrancyGuard} from "@cm/utils/async-reentrancy-guard"
import {assertNever, REF_COUNT_DEBUG_ID_INITIAL} from "@cm/utils"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {TilingCanvasComponent} from "@app/textures/texture-editor/tiling-canvas/tiling-canvas.component"
import {Operator, OperatorExecutionMode, OperatorType} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {OperatorShift} from "@app/textures/texture-editor/operator-stack/operators/shift/operator-shift"
import {
    getSourceMapDataObjectIds,
    TextureEditorCallback,
    TextureSetRevisionSpecific,
    TextureTypeData,
} from "@app/textures/texture-editor/texture-editor-callback"
import {TextureEditorDataService} from "@app/textures/texture-editor/texture-editor-data.service"
import {DownloadFormat} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-data-object"
import {TexturesApiService} from "@app/textures/service/textures-api.service"
import {OperatorInitial} from "@app/textures/texture-editor/operator-stack/operators/initial/operator-initial"
import {OperatorTiling} from "@app/textures/texture-editor/operator-stack/operators/tiling/operator-tiling"
import {OperatorRotate} from "@app/textures/texture-editor/operator-stack/operators/rotate/operator-rotate"
import {OperatorLayerAndMask} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/operator-layer-and-mask"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {OperatorTest} from "@app/textures/texture-editor/operator-stack/operators/test/operator-test"
import {OperatorHighpass} from "@app/textures/texture-editor/operator-stack/operators/highpass/operator-highpass"
import {OperatorRecolor} from "@app/textures/texture-editor/operator-stack/operators/recolor/operator-recolor"
import {OperatorCloneStamp} from "@app/textures/texture-editor/operator-stack/operators/clone-stamp/operator-clone-stamp"
import {outputToObservable} from "@angular/core/rxjs-interop"
import {DeferredBatchCall} from "@cm/browser-utils"
import {TextureType} from "@generated"
import {TextureTypeLabels} from "@platform/models/state-labels/texture-type-labels"
import {AnyImageOpHal, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeLevels} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/levels/node"
import {ImageOpNodeBagSet} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/image-op-node-bag-set"
import {ImageOpNodeShowTiles} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/show-tiles/node"
import {OperatorStackImageOpSystem} from "@app/textures/texture-editor/operator-stack/operator-stack-image-op-system"
import {HalContext} from "@cm/hal"
import {AnyImageOpResultNode, ImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-system"
import {ImageOpNodeOutput} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/output-node/node"
import {ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {createSourceDataObjectNode} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/source-data-object-node"
import {TextureTileLoaderService} from "@app/common/services/texture-tile-loader/texture-tile-loader.service"

const REF_COUNT_DEBUG_ID_CURRENTLY_SELECTED_OPERATOR_INPUT = "OperatorStack.currentlySelectedOperatorInput"

export class OperatorStack implements OperatorCallback {
    readonly selectedOperatorChanged = new EventEmitter<Operator | null>() // OperatorCallback
    readonly resultImageChanged = new EventEmitter<AnyImageOpHal | null>()

    private readonly logger: CmLogger

    constructor(
        private textureEditorCallback: TextureEditorCallback,
        public injector: Injector,
        readonly tilingCanvas: TilingCanvasComponent,
    ) {
        this.logger = injector.get(CM_LOGGER_TEXTURES)
        this._texturesApi = injector.get(TexturesApiService)
        this._notificationService = injector.get(NotificationsService)
        this._dataService = injector.get(TextureEditorDataService)
        this._halContext = this.tilingCanvas.halContext

        this._effectRef = effect(
            () => {
                this.$sourceDataSyncTrigger()
                untracked(() => this.enqueueSourceDataSync())
            },
            {injector: this.injector},
        )

        outputToObservable(this.tilingCanvas.frameDrawn)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(() => {
                if (this.scheduleDeferredUpdateResultImageAfterPaint) {
                    this.scheduleDeferredUpdateResultImageAfterPaint = false
                    this.deferredUpdateResultImage.schedule()
                }
            })

        const textureTileLoader = injector.get(TextureTileLoaderService)
        this._imageOpEvaluatorPromiseGate = new AsyncReentrancyGuard.PromiseGate()
        this._imageOpSystem = new OperatorStackImageOpSystem(this._halContext, this.texturesApi, textureTileLoader)
        this._imageOpSystem.sourceImageChanged.pipe(takeUntil(this.unsubscribe)).subscribe(async () => this.requestUpdateResultImage())
    }

    async dispose(): Promise<void> {
        function waitForQueuedMicrotasksToDrain(): Promise<void> {
            return new Promise((resolve) => setTimeout(resolve, 0))
        }

        this.muteUpdates = true
        this._effectRef.destroy()
        await this._sourceDataSyncQueue // let already requested source/edits syncs finish before tearing down what they work on
        this.unsubscribe.next()
        this.unsubscribe.complete()
        this.deferredUpdateResultImage.cancel()
        await this._lastUpdateResultImagePromise?.catch(() => {}) // let an in-flight evaluate settle before tearing down the HAL/GL resources it's using
        while (this.$_operators().length > 0) {
            this.removeOperator(this.$_operators()[this.$_operators().length - 1])
        }
        this.setCurrentlySelectedOperatorInput(undefined)
        this.resultImageChanged.emit(null)
        this._imageOpNodeBagSet.dispose()
        this._imageOpSystem?.dispose()
        this._imageOpSystem = null
        this._halContext = null

        await waitForQueuedMicrotasksToDrain() // ImageOpNode.dispose() releases inputs via queueMicrotask(), so the cascade above isn't done yet

        if (ImageOpNode.instances.size > 0) {
            this.logger.warn("Not all ImageOpNode instances were disposed")
            ImageOpNode.printDebugInfo()
        }
        if (ImageOpHal.debugInfoByClassName.size > 0) {
            this.logger.warn("Not all ImageOpHal instances were disposed")
            ImageOpHal.printDebugInfo()
        }
        if (ImageOpHalData.instances.size > 0) {
            this.logger.warn("Not all ImageOpHalData instances were disposed")
            ImageOpHalData.printDebugInfo()
        }
    }

    get isDebugEnabled(): boolean {
        return this.textureEditorCallback.isDebugEnabled
    }

    get revision(): TextureSetRevisionSpecific {
        const revision = this._dataService.$revision()
        if (!revision) {
            throw Error("revision is null")
        }
        return revision
    }

    get textureTypeData(): TextureTypeData | undefined {
        return this._dataService.$textureTypeData()
    }

    get texturesApi(): TexturesApiService {
        return this._texturesApi
    }

    get organization() {
        const organization = this.revision.textureSet.textureGroup.organization
        return {
            id: organization.id,
            legacyId: organization.legacyId,
        }
    }

    readonly $isBusy = computed(() => this.$_busyCounter() > 0)

    get resultImageError(): Error | null {
        return this.$_resultImageError()
    }

    clearResultImageError(): void {
        this.$_resultImageError.set(null)
    }

    readonly $isEdited = computed(() => this.$_operatorsChanged() || this.$_operators().some((operator) => operator.$edited()))

    readonly $isValid = computed(() => this.$_operators().every((operator) => operator.$valid()))

    get muteUpdates(): boolean {
        return this._updatesMutedCounter > 0
    }

    set muteUpdates(value: boolean) {
        if (value) {
            if (this._updatesMutedCounter === 0) {
                this._mutedUpdatePending = this.deferredUpdateResultImage.isPending
                this.deferredUpdateResultImage.cancel()
            }
            this._updatesMutedCounter++
        } else {
            this._updatesMutedCounter--
            if (this._updatesMutedCounter < 0) {
                throw Error("Mismatched muteUpdates calls")
            }
            if (this._updatesMutedCounter === 0) {
                if (this._mutedUpdatePending) {
                    this._mutedUpdatePending = false
                    this.requestUpdateResultImage()
                }
            }
        }
    }

    // OperatorCallback
    get halContext(): HalContext {
        if (!this._halContext) {
            throw new Error("OperatorStack: Already disposed")
        }
        return this._halContext
    }

    get imageOpSystem() {
        if (!this._imageOpSystem) {
            throw new Error("OperatorStack: Already disposed")
        }
        return this._imageOpSystem
    }

    get operators(): readonly Operator[] {
        return this.$_operators()
    }

    // OperatorCallback
    notifyOperatorEdited(operator: Operator): void {
        const index = this.$_operators().indexOf(operator)
        if (index !== -1) {
            this.invalidateDownstreamCaches(index + 1)
        }
    }

    private invalidateDownstreamCaches(fromIndex: number): void {
        const operators = this.$_operators()
        for (let i = fromIndex; i < operators.length; i++) {
            operators[i].invalidateUpstreamCaches()
        }
    }

    get selectedOperator(): Operator | null {
        return this.$_selectedOperator()
    }

    set selectedOperator(value: Operator | null) {
        if (this.$_selectedOperator() === value) {
            return
        }
        this.$_selectedOperator.set(value)
        this.requestUpdateResultImage()
        this.selectedOperatorChanged.emit(this.$_selectedOperator())
    }

    get selectedOperatorInput(): AnyImageOpResultNode | undefined {
        return this._currentlySelectedOperatorInput
    }

    resetChangedFlag() {
        this.$_operatorsChanged.set(false)
    }

    async setTextureEdits(edits: TextureEditNodes.TextureEdits | null) {
        this.beginBulkChange()
        try {
            await this.resetAllOperators()
            if (edits) {
                for (const operator of edits.operators) {
                    await this.createOperator(operator)
                }
                this.selectedOperator = this.$_operators().length > 0 ? this.$_operators()[this.$_operators().length - 1] : null
            }
            this.resetChangedFlag()
        } finally {
            this.endBulkChange()
        }
    }

    async duplicateOperator(operator: Operator, selectNewOperator: boolean): Promise<Operator> {
        this.muteUpdates = true
        const clonedOperator = await operator.clone()
        const index = this.$_operators().indexOf(operator)
        await this.addOperator(clonedOperator, index + 1)
        clonedOperator.markEdited()
        if (selectNewOperator) {
            this.selectedOperator = clonedOperator
        }
        this.muteUpdates = false
        return clonedOperator
    }

    async createOperator(operatorTypeOrNode: OperatorType | TextureEditNodes.Operator): Promise<Operator> {
        this.logger.trace("createOperator - begin: ", operatorTypeOrNode)
        const operator = await this.createOperatorFromTypeOrNode(operatorTypeOrNode)
        await this.addOperator(operator)
        this.logger.trace("createOperator - end: ", operatorTypeOrNode)
        return operator
    }

    removeOperator(operator: Operator): void {
        const operators = this.$_operators()
        const operatorIndex = operators.indexOf(operator)
        if (operatorIndex < 0) {
            throw Error("operator not found")
        }
        this.onOperatorChanged(operator)
        if (this.$_selectedOperator() === operator) {
            this.selectedOperator = operatorIndex > 0 ? operators[operatorIndex - 1] : operators.length > 1 ? operators[operatorIndex + 1] : null
        }
        operator.dispose()
        this.$_operators.set([...operators.slice(0, operatorIndex), ...operators.slice(operatorIndex + 1)])
        this.invalidateDownstreamCaches(operatorIndex)
        this.requestUpdateResultImage()
    }

    async resetAllOperators() {
        while (this.$_operators().length > 0) {
            this.removeOperator(this.$_operators()[this.$_operators().length - 1])
        }
        // add and select initial operator per default
        this.selectedOperator = await this.createOperator("operator-initial")
    }

    setOperatorLocked(operator: Operator, locked: boolean): void {
        if (operator.$locked() === locked) {
            return
        }
        operator.setLocked(locked)
        this.requestUpdateResultImage()
    }

    setOperatorEnabled(operator: Operator, enabled: boolean): void {
        if (operator.$enabled() === enabled) {
            return
        }
        operator.setEnabled(enabled)
        this.onOperatorChanged(operator)
        this.requestUpdateResultImage()
    }

    private async addOperator(operator: Operator, insertAtIndex?: number): Promise<void> {
        operator.requestEvaluation.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.requestUpdateResultImage())
        const operators = this.$_operators()
        if (insertAtIndex !== undefined && insertAtIndex >= 0 && insertAtIndex < operators.length) {
            this.$_operators.set([...operators.slice(0, insertAtIndex), operator, ...operators.slice(insertAtIndex)])
            this.invalidateDownstreamCaches(insertAtIndex + 1)
        } else {
            this.$_operators.set([...operators, operator])
        }
        this.onOperatorChanged(operator)
        this.requestUpdateResultImage()
    }

    // Source image and edits must be applied one after the other, and syncs from successive data changes one after the other too:
    // the edits are created asynchronously against the current source image, so interleaving would build operators on half-switched data.
    // Updates stay muted from enqueueing until the sync has run.
    private enqueueSourceDataSync() {
        this.beginBulkChange()
        this._sourceDataSyncQueue = this._sourceDataSyncQueue.then(async () => {
            try {
                // read when it is this sync's turn, so it always applies the latest data
                const display = this._dataService.$displayDataObject()
                const revision = this._dataService.$revision()
                await this.setSourceImage(display ? {dataObjectId: display.dataObject.id, isResult: display.isResult, downloadFormat: "tiles"} : null)
                // edits only reset when the revision itself changes - a type switch or an in-place metadata mutation
                // (resize/rename/save) keeps the same textureSetRevision.id and must not clobber in-progress edits
                const revisionId = revision?.textureSetRevision.id
                if (revisionId !== this._lastSyncedRevisionId) {
                    this._lastSyncedRevisionId = revisionId
                    await this.setTextureEdits(revision?.persistedTextureEdits ?? null)
                }
            } catch (e) {
                this.logger.error("Error while updating source data", e)
                this._notificationService.showError("Error while updating source data: " + (e as Error).message)
            } finally {
                this.endBulkChange()
            }
        })
    }

    private beginBulkChange() {
        this.busyInc()
        this.muteUpdates = true
    }

    private endBulkChange(forceUpdate = false) {
        if (forceUpdate) {
            this.requestUpdateResultImage()
        }
        this.muteUpdates = false
        this.busyDec()
    }

    private async setSourceImage(data: {dataObjectId: string; isResult: boolean; downloadFormat: DownloadFormat} | null) {
        this.imageOpSystem.setSourceImageDataObjectId(
            data
                ? {
                      dataObjectId: data.dataObjectId,
                      channels: TextureTypeLabels.get(this.selectedTextureType)!.channelLayout.length,
                      downloadFormat: data.downloadFormat,
                  }
                : null,
        )
        this._sourceImageIsResult = data?.isResult ?? false
        this.requestUpdateResultImage()
    }

    private onOperatorChanged(_operator: Operator) {
        this.$_operatorsChanged.set(true)
    }

    private async createOperatorFromTypeOrNode(operatorTypeOrNode: OperatorType | TextureEditNodes.Operator) {
        const isOperatorNode = typeof operatorTypeOrNode === "object"
        const operatorType = isOperatorNode ? operatorTypeOrNode.type : operatorTypeOrNode
        const operatorNode = isOperatorNode ? operatorTypeOrNode : null
        let operator: Operator
        switch (operatorType) {
            case "operator-initial":
                operator = new OperatorInitial(this)
                break
            case "operator-shift":
                operator = new OperatorShift(this, operatorNode as TextureEditNodes.OperatorShift)
                break
            case "operator-rotate":
                operator = new OperatorRotate(this, operatorNode as TextureEditNodes.OperatorRotate)
                break
            case "operator-layer-and-mask":
                operator = new OperatorLayerAndMask(this, operatorNode as TextureEditNodes.OperatorLayerAndMask)
                break
            case "operator-highpass":
                operator = new OperatorHighpass(this, operatorNode as TextureEditNodes.OperatorHighpass)
                break
            case "operator-recolor":
                operator = new OperatorRecolor(this, operatorNode as TextureEditNodes.OperatorRecolor)
                break
            case "operator-clone-stamp":
                operator = new OperatorCloneStamp(this, operatorNode as TextureEditNodes.OperatorCloneStamp)
                break
            case "operator-tiling":
                operator = new OperatorTiling(this, operatorNode as TextureEditNodes.OperatorTiling)
                break
            case "operator-test":
                operator = new OperatorTest(this, operatorNode as TextureEditNodes.OperatorTest)
                break
            default:
                assertNever(operatorType)
        }
        await operator.init()
        return operator
    }

    requestUpdateResultImage(): void {
        if (this.muteUpdates) {
            this._mutedUpdatePending = true
        } else {
            if (this.deferredUpdateResultImage.isPending) {
                // while the canvas is redrawing already we postpone the update to not spam the canvas with updates
                this.scheduleDeferredUpdateResultImageAfterPaint = true
            } else {
                this.deferredUpdateResultImage.schedule()
            }
        }
    }

    private async updateResultImage(): Promise<void> {
        if (!this.imageOpSystem.currentSourceImage) {
            return // nothing loaded yet (or the load failed) - there is nothing to evaluate
        }
        this.busyInc()
        this.$_resultImageError.set(null)
        this._imageOpEvaluatorPromiseGate
            .startAndRejectCurrent(
                () => {
                    this.logger.trace("updateResultImage - started")
                    return this.executeOperators()
                },
                (result) => {
                    result?.resultImage.release(REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT)
                    result?.currentlySelectedOperatorInput?.release(REF_COUNT_DEBUG_ID_CURRENTLY_SELECTED_OPERATOR_INPUT)
                },
            )
            .then((result) => {
                this.logger.trace("updateResultImage - resultImage received")

                this.resultImageChanged.emit(result.resultImage)
                result.resultImage.release(REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT)

                this.setCurrentlySelectedOperatorInput(result.currentlySelectedOperatorInput)
                result.currentlySelectedOperatorInput?.release(REF_COUNT_DEBUG_ID_CURRENTLY_SELECTED_OPERATOR_INPUT)

                this.logger.trace("updateResultImage - finished")
            })
            .catch((e) => {
                if (e instanceof AsyncReentrancyGuard.RejectionError) {
                    this.logger.trace("updateResultImage - cancelled")
                } else {
                    this.$_resultImageError.set(e instanceof Error ? e : new Error(String(e)))
                    this._notificationService.showError("Error while updating result image: " + e.message)
                    setTimeout(() => {
                        throw e
                    }, 0) // Throws as an unhandled exception
                }
            })
            .finally(() => this.busyDec())
    }

    private setCurrentlySelectedOperatorInput(currentlySelectedOperatorInput: AnyImageOpResultNode | undefined) {
        this._currentlySelectedOperatorInput?.release(REF_COUNT_DEBUG_ID_CURRENTLY_SELECTED_OPERATOR_INPUT)
        this._currentlySelectedOperatorInput = currentlySelectedOperatorInput
        this._currentlySelectedOperatorInput?.addRef(REF_COUNT_DEBUG_ID_CURRENTLY_SELECTED_OPERATOR_INPUT)
    }

    private async executeOperators(): Promise<ExecuteOperatorsResult> {
        const sourceImage = this.imageOpSystem.currentSourceImage
        if (!sourceImage) {
            throw Error("sourceImage not available")
        }

        const generatedNodeGraph = await this.generateOperatorNodeGraph("preview", this.selectedTextureType, sourceImage, {
            processToSelectionOnly: true,
            sourceImageIsResult: this._sourceImageIsResult,
            retrieveCurrentlySelectedOperatorInput: true,
        })

        // evaluate graph
        const evaluatedResultImage = await this.imageOpSystem.evaluate("hal", generatedNodeGraph.resultImage)

        this.logger.trace("executeOperators - end")
        return {
            resultImage: evaluatedResultImage,
            currentlySelectedOperatorInput: generatedNodeGraph.currentlySelectedOperatorInput,
        }
    }

    // async exportOperatorNodeGraph() {
    // const sourceImage = this._imageOpSystem.currentSourceImage
    // if (!sourceImage) {
    //     throw Error("sourceImage not available")
    // }
    //
    // const generatedNodeGraph = await this.generateOperatorNodeGraph("preview", this.selectedTextureType, sourceImage, {
    //     processToSelectionOnly: true,
    //     sourceImageIsResult: this._sourceImageIsResult,
    // })
    //
    // if (generatedNodeGraph.resultImage instanceof GraphNode) {
    //     const simpleGraph = simpleGraphFromGraphNode(generatedNodeGraph.resultImage)
    //     const exporter = new ExporterYed()
    //     const xml = exporter.export(simpleGraph)
    //     downloadFromMemory("node-graph.graphml", xml)
    // }
    //
    // // evaluate graph
    // const [evaluatedResultImage] = await this._imageOpSystem.evaluate("hal", [generatedNodeGraph.resultImage])
    //
    // const simpleGraph = simpleGraphFromImageOpHal(evaluatedResultImage)
    // const exporter = new ExporterYed()
    // const xml = exporter.export(simpleGraph)
    // downloadFromMemory("hal-graph.graphml", xml)
    //
    // evaluatedResultImage.release()
    // }

    async generateOperatorNodeGraph(
        mode: OperatorExecutionMode,
        textureType: TextureType,
        sourceImage: AnyImageOpResultNode,
        options?: {
            processToSelectionOnly?: boolean
            sourceImageIsResult?: boolean
            retrieveCurrentlySelectedOperatorInput?: boolean
        },
    ): Promise<GeneratedOperatorNodeGraphResult> {
        const imageOpNodeBag = this._imageOpNodeBagSet.getImageOpNodeBag(mode, textureType)
        return imageOpNodeBag.runInFrame(() => this.generateOperatorNodeGraphInFrame(mode, textureType, sourceImage, imageOpNodeBag, options))
    }

    private async generateOperatorNodeGraphInFrame(
        mode: OperatorExecutionMode,
        textureType: TextureType,
        sourceImage: AnyImageOpResultNode,
        imageOpNodeBag: ImageOpNodeBag,
        options?: {
            processToSelectionOnly?: boolean
            sourceImageIsResult?: boolean
            retrieveCurrentlySelectedOperatorInput?: boolean
        },
    ): Promise<GeneratedOperatorNodeGraphResult> {
        const processToSelectionOnly = options?.processToSelectionOnly ?? false
        const sourceImageIsResult = options?.sourceImageIsResult ?? false
        const retrieveCurrentlySelectedOperatorInput = options?.retrieveCurrentlySelectedOperatorInput ?? false
        let currSourceImage: AnyImageOpResultNode = this.prepareSourceImage(textureType, sourceImage, imageOpNodeBag)

        let hasAppliedOperators = false
        let currentlySelectedOperatorInput: AnyImageOpResultNode | undefined = undefined
        if (!sourceImageIsResult) {
            const operatorsSnapshot = this.$_operators()
            for (let i = 0; i < operatorsSnapshot.length; i++) {
                const operator = operatorsSnapshot[i]
                this.logger.trace(`  executeOperators - operator(${i}): `, operator)
                if (retrieveCurrentlySelectedOperatorInput && operator === this.selectedOperator) {
                    currentlySelectedOperatorInput = currSourceImage
                    currentlySelectedOperatorInput.addRef(REF_COUNT_DEBUG_ID_CURRENTLY_SELECTED_OPERATOR_INPUT)
                }
                if (operator.shouldApplyTo(textureType)) {
                    hasAppliedOperators = true
                    if (!operator.getImageOpGraph) {
                        throw Error("operator does not support getImageOpGraph")
                    }
                    currSourceImage = await operator.getImageOpGraph(currSourceImage, {mode, textureType, inputChanged: true}) // TODO remove inputChanged
                }
                if (processToSelectionOnly && this.selectedOperator === operator) {
                    break
                }
            }
        }

        if (this.hasAppliedOperatorsForTextureType(textureType) && hasAppliedOperators) {
            switch (textureType) {
                case TextureType.Normal:
                    //currSourceImage = normalizeNormalMap(cmdQueue, {sourceImage: currSourceImage})    // TODO this is broken - debug !
                    break
            }
        }

        if (TextureEditorSettings.ShowTiles) {
            currSourceImage = imageOpNodeBag.getNode("ShowTiles", ImageOpNodeShowTiles, {
                sourceImage: currSourceImage,
            })
        }

        // final output node
        currSourceImage = imageOpNodeBag.getNode("output", ImageOpNodeOutput, {
            sourceImage: currSourceImage,
        })

        return {
            hasAppliedOperators,
            resultImage: currSourceImage,
            currentlySelectedOperatorInput,
        }
    }

    // Forces every operator's createImageOpGraph to run in HAL/pre-final mode against current upstream
    // state, for every texture type it applies to — not just whatever is currently selected/viewed.
    // Needed before job submission so BakedMaskCache.pending (prepare-and-bake-minimal-mask.ts) is fresh
    // everywhere final-mode baking will read it; skipping a texture type an operator applies to (via
    // Operator.shouldApplyTo/ignoreTextureTypes) would leave that operator's mask stale. Each texture
    // type loads its own source data object via "tiles" (paged/streamed, matching the live viewport)
    // rather than reusing imageOpSystem.currentSourceImage, which only ever holds the currently viewed
    // type. Selection is pinned to the last operator for the duration (bypassing the public setter, so
    // there's no UI flicker) so no upstream operator can hit a selection-gated debug/preview branch
    // (e.g. layer&mask's "show mask") and feed a wrong image downstream. Uses "pre-final" rather than
    // "preview" so operators don't eagerly (re)compute/cache preview-only statistics (e.g. highpass's
    // sourceMean, recolor's sourceStats) here — those are only ever needed for interactive preview and
    // are otherwise computed live/inline by "final" mode baking, so eagerly computing them in this
    // throwaway warm-up pass would be pure waste (see OperatorExecutionMode).
    async refreshFullStackInHalMode(): Promise<void> {
        const operators = this.$_operators()
        const lastOperator = operators.length > 0 ? operators[operators.length - 1] : null
        const previousSelection = this.$_selectedOperator()
        this.$_selectedOperator.set(lastOperator)
        try {
            const sourceMapDataObjectIdByTextureType = getSourceMapDataObjectIds(this.revision.textureSetRevision)
            for (const [textureType, dataObjectId] of sourceMapDataObjectIdByTextureType) {
                if (!this.hasAppliedOperatorsForTextureType(textureType)) {
                    continue
                }
                const sourceImage = createSourceDataObjectNode(textureType, dataObjectId, "tiles", "refresh-full-stack-source")
                try {
                    const generatedNodeGraph = await this.generateOperatorNodeGraph("pre-final", textureType, sourceImage, {
                        processToSelectionOnly: false,
                    })
                    const evaluatedResultImage = await this.imageOpSystem.evaluate("hal", generatedNodeGraph.resultImage)
                    evaluatedResultImage.release(REF_COUNT_DEBUG_ID_IMAGE_OP_SYSTEM_EVALUATE_RESULT)
                } finally {
                    sourceImage.release(REF_COUNT_DEBUG_ID_INITIAL)
                }
            }
        } finally {
            this.$_selectedOperator.set(previousSelection)
        }
    }

    get selectedTextureType(): TextureType {
        return this._dataService.$selectedTextureType() ?? TextureType.Diffuse
    }

    hasAppliedOperators(): boolean {
        return this.$_operators().some((operator) => !operator.flags.has("is-internal"))
    }

    hasAppliedOperatorsForTextureType(textureType: TextureType): boolean {
        const operators = this.$_operators()
        for (let i = 0; i < operators.length; i++) {
            const operator = operators[i]
            if (!operator.flags.has("is-internal") && operator.shouldApplyTo(textureType)) {
                return true
            }
        }
        return false
    }

    prepareSourceImage(textureType: TextureType, sourceImage: AnyImageOpResultNode, imageOpNodeBag: ImageOpNodeBag) {
        if (this.hasAppliedOperatorsForTextureType(textureType)) {
            // apply levels to ensure no negative values. we only do this if we have applied operators to keep the graph trivial otherwise (for further down optimizations)
            sourceImage = imageOpNodeBag.getNode("removeNegativeValues", ImageOpNodeLevels, {
                sourceImage: sourceImage,
                blackLevel: 0.0,
                whiteLevel: 1.0,
            })
        }
        return sourceImage
    }

    // OperatorCallback
    setBusy(busy: boolean): void {
        if (busy) {
            this.busyInc()
        } else {
            this.busyDec()
        }
    }

    private busyInc() {
        if (this.$_busyCounter() === 0) {
            this.muteUpdates = true
        }
        this.$_busyCounter.update((v) => v + 1)
    }

    private busyDec() {
        this.$_busyCounter.update((v) => v - 1)
        if (this.$_busyCounter() === 0) {
            this.muteUpdates = false
        }
    }

    private readonly unsubscribe = new Subject<void>()
    private readonly $_busyCounter = signal(0)
    private readonly $_resultImageError = signal<Error | null>(null)
    private _updatesMutedCounter = 0
    private _mutedUpdatePending = false
    private _sourceImageIsResult = true
    private _halContext: HalContext | null
    private readonly $_operatorsChanged = signal(false)
    private readonly $_operators = signal<readonly Operator[]>([])
    private readonly $_selectedOperator = signal<Operator | null>(null)
    private _currentlySelectedOperatorInput: AnyImageOpResultNode | undefined = undefined
    private readonly deferredUpdateResultImage = new DeferredBatchCall(() => {
        this._lastUpdateResultImagePromise = this.updateResultImage()
    })
    private _lastUpdateResultImagePromise: Promise<void> | undefined
    private scheduleDeferredUpdateResultImageAfterPaint = false
    private readonly _texturesApi: TexturesApiService
    private readonly _notificationService: NotificationsService
    private readonly _dataService: TextureEditorDataService
    private readonly _effectRef: EffectRef
    private _sourceDataSyncQueue: Promise<void> = Promise.resolve()
    // only what the sync depends on - an in-place revision update (rename/resize/save) keeps the same key and must not re-trigger it
    private readonly $sourceDataSyncTrigger = computed(() => {
        const display = this._dataService.$displayDataObject()
        return `${this._dataService.$revision()?.textureSetRevision.id}|${display?.dataObject.id}|${display?.isResult}`
    })
    private _lastSyncedRevisionId: string | undefined

    private _imageOpSystem: OperatorStackImageOpSystem | null
    private readonly _imageOpEvaluatorPromiseGate: AsyncReentrancyGuard.PromiseGate<ExecuteOperatorsResult>
    private _imageOpNodeBagSet = new ImageOpNodeBagSet()
}

type ExecuteOperatorsResult = {
    resultImage: AnyImageOpHal
    currentlySelectedOperatorInput: AnyImageOpResultNode | undefined
}

export type GeneratedOperatorNodeGraphResult = {
    hasAppliedOperators: boolean
    resultImage: AnyImageOpResultNode
    currentlySelectedOperatorInput: AnyImageOpResultNode | undefined
}
